// pages/payroll/EmployeeLoanSinglePrint.jsx
import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const BASE_URL = "http://nissi-india.com";

const n = (v) => (v === null || v === undefined ? "" : String(v));

const money = (v) => {
  const x = Number(v);
  if (!Number.isFinite(x)) return n(v);
  return x.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const EmployeeLoanSinglePrint = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  const finYearId = params.get("finYearId") || localStorage.getItem("finYearId") || "";
  const loanId = params.get("loanId") || ""; // ✅ must come from listing row print

  // ✅ STATIC HEADER (no server call)
  const HEADER = useMemo(
    () => ({
      name: "Nissi Infotech",
      line1: "Nissi",
      line2: "CIVIL CONSTRUCTION",
      address: "3/42-12, Nagappa complex, kavundampalayam - 641030 , Tamil Nadu. India.",
      phone: "9003744644",
      email: "infraconsoft-implementation@nissiinfotech.in",
      logoText: "NISSI INFOTECH PVT LTD",
    }),
    []
  );

  const [loading, setLoading] = useState(true);
  const [head, setHead] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalDeduction, setTotalDeduction] = useState(0);

  const fetchData = async () => {
    if (!String(finYearId).trim()) {
      setLoading(false);
      return;
    }
    if (!String(loanId).trim()) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-loan-report/single-print`, {
        params: { finYearId: Number(finYearId), loanId },
      });

      if (res?.data?.success) {
        setHead(res.data.head || null);
        setRows(res.data.rows || []);
        setTotalDeduction(Number(res.data?.totals?.totalDeduction || 0));
      } else {
        setHead(null);
        setRows([]);
        setTotalDeduction(0);
      }
    } catch {
      setHead(null);
      setRows([]);
      setTotalDeduction(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading) setTimeout(() => window.print(), 300);
  }, [loading]);

  return (
    <div>
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; font-family: Arial, Helvetica, sans-serif; }
        .page { padding: 16px; }
        .sheet { border: 2px solid #111; padding: 14px; }

        .headerBox {
          border: 2px solid #111;
          padding: 10px;
          display: grid;
          grid-template-columns: 150px 1fr;
          gap: 10px;
          align-items: center;
          margin-bottom: 12px;
        }
        .logo {
          width: 140px;
          height: 90px;
          border: 2px solid #111;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          font-size: 10px;
          font-weight: 800;
          background: #f1f1f1;
          padding: 6px;
          line-height: 1.1;
        }
        .hName { text-align: center; font-size: 22px; font-weight: 800; margin: 0; }
        .hSub { text-align: center; font-size: 13px; line-height: 1.25; }
        .hSub b { font-weight: 800; }

        .titleLine { border-top: 2px solid #111; margin: 10px 0; }
        .title { text-align: center; font-size: 18px; font-weight: 800; padding: 6px 0; }

        .infoBox {
          border: 1.6px solid #111;
          padding: 10px 12px;
          margin-bottom: 12px;
        }
        .infoGrid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px 40px;
          font-size: 13px;
        }
        .row { display: grid; grid-template-columns: 140px 12px 1fr; align-items: baseline; }
        .lbl { font-weight: 700; }
        .sep { text-align: center; }
        .val { font-weight: 700; }

        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1.6px solid #111; padding: 8px 6px; }
        thead th { font-weight: 800; text-align: center; }
        td.center { text-align: center; }
        td.left { text-align: left; }
        td.right { text-align: right; }

        .totalRow td { font-weight: 800; }
        .sigRow {
          margin-top: 28px;
          display: grid;
          grid-template-columns: 1fr 1fr 1fr 1fr;
          gap: 10px;
          font-size: 12px;
          font-weight: 700;
        }
        .sig { padding-top: 18px; border-top: 1.6px solid #111; text-align: center; }

        @media print {
          .noPrint { display: none !important; }
          .page { padding: 0; }
        }
      `}</style>

      <div className="page">
        <div className="sheet">
          {/* STATIC HEADER */}
          <div className="headerBox">
            <div className="logo">{HEADER.logoText}</div>

            <div>
              <p className="hName">{HEADER.name}</p>
              <div className="hSub">
                <div>{HEADER.line1}</div>
                <div><b>{HEADER.line2}</b></div>
                <div>{HEADER.address}</div>
                <div>Phone No : {HEADER.phone}</div>
                <div>{HEADER.email}</div>
              </div>
            </div>
          </div>

          <div className="titleLine" />
          <div className="title">EMPLOYEE LOAN REPORT</div>

          {loading ? (
            <div style={{ padding: 16, textAlign: "center" }}>Loading...</div>
          ) : !head ? (
            <div style={{ padding: 16, textAlign: "center" }}>No Records Found</div>
          ) : (
            <>
              {/* Summary block */}
              <div className="infoBox">
                <div className="infoGrid">
                  <div className="row">
                    <div className="lbl">Employee Code</div><div className="sep">:</div><div className="val">{n(head.fld_Employeecode)}</div>
                  </div>
                  <div className="row">
                    <div className="lbl">Employee Name</div><div className="sep">:</div><div className="val">{n(head.fld_Employee)}</div>
                  </div>

                  <div className="row">
                    <div className="lbl">Location</div><div className="sep">:</div><div className="val">{n(head.fld_BranchName)}</div>
                  </div>
                  <div className="row">
                    <div className="lbl">Advance Amount</div><div className="sep">:</div><div className="val">{money(head.fld_LoanAmount)}</div>
                  </div>

                  <div className="row">
                    <div className="lbl">Cummulative Amount</div><div className="sep">:</div><div className="val">{money(head.fld_LoanAmount)}</div>
                  </div>
                  <div className="row">
                    <div className="lbl">Deduction Amount</div><div className="sep">:</div><div className="val">{money(head.fld_AmountPerMonth)}</div>
                  </div>

                  <div className="row">
                    <div className="lbl">Total Month</div><div className="sep">:</div><div className="val">{n(head.fld_TotalMonth)}</div>
                  </div>
                  <div className="row">
                    <div className="lbl">Balance Month</div><div className="sep">:</div><div className="val">{n(head.fld_balancemonth)}</div>
                  </div>

                  <div className="row">
                    <div className="lbl">Advance Date</div><div className="sep">:</div><div className="val">{n(head.LoanDate)}</div>
                  </div>
                  <div />
                </div>
              </div>

              {/* Details table */}
              <div style={{ overflow: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 55 }}>S.No</th>
                      <th style={{ width: 80 }}>Year</th>
                      <th style={{ width: 120 }}>Month</th>
                      <th>Advance Amount</th>
                      <th>Deduction Amount</th>
                      <th>Balance Amount</th>
                      <th style={{ width: 120 }}>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <tr key={idx}>
                        <td className="center">{idx + 1}</td>
                        <td className="center">{n(r.fld_paymentyear)}</td>
                        <td className="center">{n(r.fld_paymentmonth)}</td>
                        <td className="right">{money(r.advanceAmount)}</td>
                        <td className="right">{money(r.deduction)}</td>
                        <td className="right">{money(r.balanceAmount)}</td>
                        <td className="left"></td>
                      </tr>
                    ))}

                    {rows.length > 0 && (
                      <tr className="totalRow">
                        <td colSpan={4} className="right">
                          Total Deduction Amount
                        </td>
                        <td className="right">{money(totalDeduction)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Signature row */}
              <div className="sigRow">
                <div className="sig">Prepared By</div>
                <div className="sig">Checked By</div>
                <div className="sig">Sr.Manager Accounts</div>
                <div className="sig">Employee Signature</div>
              </div>
            </>
          )}
        </div>

        <div className="noPrint" style={{ textAlign: "center", marginTop: 14 }}>
          <button onClick={() => window.print()} style={{ padding: "10px 18px", cursor: "pointer" }}>
            Print
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeLoanSinglePrint;